import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Structure from the body region pool
export interface BodyRegionStructure {
  structure_key: string;
  display_name: string;
  body_region: string;
  section_token: string;
}

// Structure assigned to an exam template
export interface AssignedStructure {
  structure_key: string;
  display_name: string;
  section_token: string;
  enforcement_policy?: string;
}

interface UseBodyRegionStructuresResult {
  structures: BodyRegionStructure[];
  loading: boolean;
  fetch: (bodyRegion: string) => Promise<void>;
}

interface UseAssignedStructuresResult {
  structures: AssignedStructure[];
  loading: boolean;
  fetch: (examFamilyId: string) => Promise<void>;
}

interface UseStructureSearchResult {
  results: BodyRegionStructure[];
  loading: boolean;
  search: (query: string) => Promise<void>;
  clear: () => void;
}

/**
 * Hook for fetching structures available for a body region from body_region_structure_pool
 * This is the superset of all structures available for a given anatomical region
 * Uses prefix matching to find all sub-regions (e.g., "PELVIS" matches "PELVIS_PROSTATE", "PELVIS_BLADDER")
 */
export function useBodyRegionStructures(): UseBodyRegionStructuresResult {
  const [structures, setStructures] = useState<BodyRegionStructure[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (bodyRegion: string) => {
    setLoading(true);
    try {
      // Query body_region_structure_pool using prefix matching
      // This allows "PELVIS" to match "PELVIS_PROSTATE", "PELVIS_BLADDER", etc.
      const { data: poolData, error: poolError } = await supabase
        .from("body_region_structure_pool")
        .select("structure_key, body_region, section_token")
        .or(`body_region.eq.${bodyRegion},body_region.ilike.${bodyRegion}_%`)
        .eq("is_active", true);

      if (poolError) throw poolError;

      if (!poolData || poolData.length === 0) {
        setStructures([]);
        return;
      }

      // Get structure details from structure_registry
      const structureKeys = poolData.map((p) => p.structure_key);
      const { data: registryData, error: registryError } = await supabase
        .from("structure_registry")
        .select("structure_key, display_name")
        .in("structure_key", structureKeys)
        .eq("is_active", true);

      if (registryError) throw registryError;

      // Build lookup map
      const displayNameMap = new Map(
        (registryData || []).map((r) => [r.structure_key, r.display_name])
      );

      // Combine pool data with display names
      const result: BodyRegionStructure[] = poolData.map((p) => ({
        structure_key: p.structure_key,
        display_name: displayNameMap.get(p.structure_key) || p.structure_key,
        body_region: p.body_region,
        section_token: p.section_token,
      }));

      // Sort by section_token then display_name
      result.sort((a, b) => {
        if (a.section_token !== b.section_token) {
          return a.section_token.localeCompare(b.section_token);
        }
        return a.display_name.localeCompare(b.display_name);
      });

      setStructures(result);
    } catch (err) {
      console.error("Failed to fetch body region structures:", err);
      toast.error("Failed to load structures");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    structures,
    loading,
    fetch,
  };
}

/**
 * Hook for fetching structures already assigned to an exam template via section_structure_map
 */
export function useAssignedStructures(): UseAssignedStructuresResult {
  const [structures, setStructures] = useState<AssignedStructure[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (examFamilyId: string) => {
    setLoading(true);
    try {
      // Query section_structure_map for this exam
      const { data: mapData, error: mapError } = await supabase
        .from("section_structure_map")
        .select("structure_key, section_token, enforcement_policy")
        .eq("exam_family_id", examFamilyId);

      if (mapError) throw mapError;

      if (!mapData || mapData.length === 0) {
        setStructures([]);
        return;
      }

      // Get structure details from structure_registry
      const structureKeys = mapData.map((m) => m.structure_key);
      const { data: registryData, error: registryError } = await supabase
        .from("structure_registry")
        .select("structure_key, display_name")
        .in("structure_key", structureKeys);

      if (registryError) throw registryError;

      // Build lookup map
      const displayNameMap = new Map(
        (registryData || []).map((r) => [r.structure_key, r.display_name])
      );

      // Combine map data with display names
      const result: AssignedStructure[] = mapData.map((m) => ({
        structure_key: m.structure_key,
        display_name: displayNameMap.get(m.structure_key) || m.structure_key,
        section_token: m.section_token,
        enforcement_policy: m.enforcement_policy || undefined,
      }));

      // Sort by section_token then display_name
      result.sort((a, b) => {
        if (a.section_token !== b.section_token) {
          return a.section_token.localeCompare(b.section_token);
        }
        return a.display_name.localeCompare(b.display_name);
      });

      setStructures(result);
    } catch (err) {
      console.error("Failed to fetch assigned structures:", err);
      toast.error("Failed to load assigned structures");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    structures,
    loading,
    fetch,
  };
}

/**
 * Hook for cross-modality structure search across all body regions
 * Allows users to find and add structures from any body region
 */
export function useStructureSearch(): UseStructureSearchResult {
  const [results, setResults] = useState<BodyRegionStructure[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Search in body_region_structure_pool by structure_key or body_region
      const { data: poolData, error: poolError } = await supabase
        .from("body_region_structure_pool")
        .select("structure_key, body_region, section_token")
        .eq("is_active", true)
        .or(`structure_key.ilike.%${query}%,body_region.ilike.%${query}%`)
        .limit(100);

      if (poolError) throw poolError;

      if (!poolData || poolData.length === 0) {
        // Also try searching structure_registry directly
        const { data: registryData, error: registryError } = await supabase
          .from("structure_registry")
          .select("structure_key, display_name")
          .eq("is_active", true)
          .ilike("display_name", `%${query}%`)
          .limit(50);

        if (registryError) throw registryError;

        if (!registryData || registryData.length === 0) {
          setResults([]);
          return;
        }

        // Get body regions for these structures
        const structureKeys = registryData.map((r) => r.structure_key);
        const { data: poolForRegistry } = await supabase
          .from("body_region_structure_pool")
          .select("structure_key, body_region, section_token")
          .in("structure_key", structureKeys)
          .eq("is_active", true);

        const poolMap = new Map(
          (poolForRegistry || []).map((p) => [
            p.structure_key,
            { body_region: p.body_region, section_token: p.section_token },
          ])
        );

        const result: BodyRegionStructure[] = registryData
          .filter((r) => poolMap.has(r.structure_key))
          .map((r) => ({
            structure_key: r.structure_key,
            display_name: r.display_name,
            body_region: poolMap.get(r.structure_key)!.body_region,
            section_token: poolMap.get(r.structure_key)!.section_token,
          }));

        setResults(result);
        return;
      }

      // Get structure details from structure_registry
      const structureKeys = poolData.map((p) => p.structure_key);
      const { data: registryData, error: registryError } = await supabase
        .from("structure_registry")
        .select("structure_key, display_name")
        .in("structure_key", structureKeys)
        .eq("is_active", true);

      if (registryError) throw registryError;

      // Build lookup map
      const displayNameMap = new Map(
        (registryData || []).map((r) => [r.structure_key, r.display_name])
      );

      // Combine and filter results that match the display name
      const result: BodyRegionStructure[] = poolData
        .map((p) => ({
          structure_key: p.structure_key,
          display_name: displayNameMap.get(p.structure_key) || p.structure_key,
          body_region: p.body_region,
          section_token: p.section_token,
        }))
        .filter(
          (r) =>
            r.display_name.toLowerCase().includes(query.toLowerCase()) ||
            r.body_region.toLowerCase().includes(query.toLowerCase()) ||
            r.structure_key.toLowerCase().includes(query.toLowerCase())
        );

      // Sort by relevance (exact matches first, then by body_region)
      result.sort((a, b) => {
        const aExact = a.display_name.toLowerCase().startsWith(query.toLowerCase());
        const bExact = b.display_name.toLowerCase().startsWith(query.toLowerCase());
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return a.body_region.localeCompare(b.body_region);
      });

      setResults(result.slice(0, 50)); // Limit results
    } catch (err) {
      console.error("Failed to search structures:", err);
      toast.error("Failed to search structures");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setResults([]);
  }, []);

  return {
    results,
    loading,
    search,
    clear,
  };
}
